import { useEffect, useState } from 'react';
import axios from 'config/axios';
import type { FillAppSchema } from 'global/fillapp';
import { DEFAULT_CYCLE_YEAR } from 'global/fillapp';

// Bump the version segment when the schema shape changes in a way that
// stale localStorage caches would render incorrectly.
const CACHE_KEY_PREFIX = 'fillapp.schema.v22.';

export function useFillAppSchema(cycleYear: number = DEFAULT_CYCLE_YEAR) {
    const [schema, setSchema] = useState<FillAppSchema | null>(() => {
        try {
            const cached = localStorage.getItem(CACHE_KEY_PREFIX + cycleYear);
            return cached ? JSON.parse(cached) : null;
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(schema === null);
    const [error, setError] = useState<string | null>(null);

    // Stale-while-revalidate: render the cached copy immediately (if any) but
    // always refetch, so a stale cache entry self-heals on the next page load.
    useEffect(() => {
        let cancelled = false;
        axios.get(`fillapp/schema/${cycleYear}/`)
            .then(res => {
                if (cancelled) return;
                const fresh = JSON.stringify(res.data);
                try {
                    if (localStorage.getItem(CACHE_KEY_PREFIX + cycleYear) !== fresh) {
                        localStorage.setItem(CACHE_KEY_PREFIX + cycleYear, fresh);
                    }
                } catch {
                    // localStorage full or unavailable — just skip caching
                }
                setSchema(res.data);
                setLoading(false);
            })
            .catch(err => {
                if (cancelled) return;
                // Keep the cached copy (if any) on fetch failure.
                setError(err?.response?.data?.message || 'Failed to load schema.');
                setLoading(false);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cycleYear]);

    return { schema, loading, error };
}
